import React, { useState, useEffect, useRef } from 'react';
import { Container, Typography, Grid, Paper, Button, Stack, Box, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton } from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarDensitySelector } from '@mui/x-data-grid';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaEdit, FaTrash, FaCheckCircle, FaEye, FaFileExcel, FaPlusCircle, FaList } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import 'react-toastify/dist/ReactToastify.css';
import Loader from '../components/common/Loader';
import Iconify from '../components/iconify';
import { getProjectChapterStatus, updateDataAsset, updateDepartmentVideo, updateProjectChapter, saveChapterContents, getChapterContentEditing } from '../Servies/AdmiServices';

// Custom Toolbar Component with Export to Excel
function CustomToolbar({ chapters, projectData }) {
  const handleExportToExcel = () => {
    try {
      // Prepare data for export
      const exportData = chapters.map((chapter) => ({
        'Chapter Name': chapter.chapterName || '-',
        'Designer Name': chapter.designerName || '-',
        'PPTs (Uploaded / Total)': `${(chapter.noOfPpt || 0) - (chapter.noOfPptLeft || 0)} / ${chapter.noOfPpt || 0}`,
        'Videos (Uploaded / Total)': `${(chapter.noOfVideo || 0) - (chapter.noOfVideoLeft || 0)} / ${chapter.noOfVideo || 0}`,
        'PPT Status': chapter.pptStatus || '-',
        'Video Status': chapter.videoStatus || '-',
        'PPT Video Start Date': chapter.pptVideoStartDate ? new Date(chapter.pptVideoStartDate).toLocaleDateString() : '-',
        'PPT Video End Date': chapter.pptVideoEndDate ? new Date(chapter.pptVideoEndDate).toLocaleDateString() : '-',
        'Microplan Received': chapter.microplanRecieved || '-',
        'Microplan Name': chapter.microplanName || '-',
        'Microplan Date': chapter.microplanDate ? new Date(chapter.microplanDate).toLocaleDateString() : '-',
        'Recording Status': chapter.recordingStatus || '-',
        'Editing Status': chapter.editingStatus || '-',
        'Dubbing Status': chapter.dubbingStatus || '-',
        'English Editing Status': chapter.englishEditingStatus || '-',
        'Overall Status': chapter.status || '-',
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const colWidths = [
        { wch: 12 },  // Chapter Name
        { wch: 20 },  // Designer Name
        { wch: 22 },  // PPTs (Uploaded / Total)
        { wch: 22 },  // Videos (Uploaded / Total)
        { wch: 15 },  // PPT Status
        { wch: 15 },  // Video Status
        { wch: 20 },  // PPT Video Start Date
        { wch: 20 },  // PPT Video End Date
        { wch: 18 },  // Microplan Received
        { wch: 20 },  // Microplan Name
        { wch: 18 },  // Microplan Date
        { wch: 18 },  // Recording Status
        { wch: 15 },  // Editing Status
        { wch: 15 },  // Dubbing Status
        { wch: 22 },  // English Editing Status
        { wch: 15 }   // Overall Status
      ];
      ws['!cols'] = colWidths;

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Project Chapters');

      // Generate Excel file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      // Save file
      const fileName = `${projectData?.name || 'Project'}_Chapters_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, fileName);

      toast.success('Excel file exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export Excel file');
    }
  };

  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <Button
        size="small"
        startIcon={<FaFileExcel />}
        onClick={handleExportToExcel}
        sx={{ ml: 1 }}
      >
        Export to Excel
      </Button>
    </GridToolbarContainer>
  );
}

export default function ProjectChapters() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [projectData, setProjectData] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [allChapters, setAllChapters] = useState([]);

  // Dialog state for updating completion status
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [statusDialogData, setStatusDialogData] = useState({
    type: '', // 'PPT', 'VIDEO', 'RECORDING', 'EDITING', 'DUBBING', 'ENGLISH_EDIT'
    projectChapterId: null,
    noOfContent: '',
    completedBy: '',
  });

  // Dialog state for adding chapter contents
  const [openContentDialog, setOpenContentDialog] = useState(false);
  const [selectedChapterId, setSelectedChapterId] = useState(null);
  const [selectedChapterName, setSelectedChapterName] = useState('');
  const [loadedContents, setLoadedContents] = useState([]);
  const [showContentEditView, setShowContentEditView] = useState(false);
  const contentFileInputRef = useRef(null);

  const queryParams = new URLSearchParams(location.search);
  const projectId = queryParams.get('projectId');

  useEffect(() => {
    if (projectId) fetchProjectChapters(projectId);
  }, [projectId]);

  const fetchProjectChapters = async (id) => {
    setIsLoading(true);
    try {
      const res = await getProjectChapterStatus(id);
      if (res?.data?.status === 'SUCCESS') {
        const data = res.data.response;
        setProjectData(data);

        // Combine designing and editing data into single array
        const combinedChapters = (data.designing || []).map((designChapter) => {
          // Find matching editing chapter by projectChapterId
          const editChapter = (data.editing || []).find(
            (edit) => edit.projectChapterId === designChapter.projectChapterId
          );

          return {
            ...designChapter,
            recordingStatus: editChapter?.recordingStatus || '',
            editingStatus: editChapter?.editingStatus || '',
            dubbingStatus: editChapter?.dubbingStatus || '',
            englishEditingStatus: editChapter?.englishEditingStatus || '',
          };
        });

        setChapters(combinedChapters);
        setAllChapters(combinedChapters);
      } else {
        toast.error(res?.data?.message || 'Failed to fetch chapters');
      }
    } catch (error) {
      toast.error('Error fetching project data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateChapter = (projectId, projectName) => {
    navigate(`/dashboard/createchapter?projectId=${projectId}&projectName=${encodeURIComponent(projectName)}`);
  };

  const handleViewChapter = (projectChapterId) => {
    const chapter = chapters.find(c => c.projectChapterId === projectChapterId);
    const chapterName = chapter?.chapterName || '';
    const projectName = projectData?.name || '';
    navigate(`/dashboard/chapterhistory?projectId=${projectId}&projectChapterId=${projectChapterId}&tab=designing&projectName=${encodeURIComponent(projectName)}&chapterName=${encodeURIComponent(chapterName)}`);
  };

  const handleEditChapter = (id) => {
    navigate(`/dashboard/UpdateChapterDetails?&projectId=${projectId}&projectChapterId=${id}`);
  };

  const handleDeleteChapter = (id) => {
    toast.error(`Delete functionality for Chapter ID: ${id} - Coming soon`);
  };

  // Open dialog for adding contents
  const handleOpenContentDialog = async (projectChapterId, chapterName) => {
    setSelectedChapterId(projectChapterId);
    setSelectedChapterName(chapterName);
    setIsLoading(true);

    try {
      // Fetch existing contents
      const response = await getChapterContentEditing(projectChapterId);

      if (response?.data?.status === 'SUCCESS' && response.data.response?.length > 0) {
        // Map API response to our content structure with ALL fields
        const existingContents = response.data.response.map((content, index) => ({
          id: content.id || Date.now() + index,
          projectChapterId: content.projectChapterId,
          contentTitle: content.contentTitle || '',
          contentDescription: content.contentDescription || '',
          subject: content.subject || '',
          contentType: content.contentType || '',
          contentUrl: content.contentUrl || '',
          chapterName: content.chapterName || '',
          teacher: content.teacher || '',
          timing: content.timing || '',
          recordingDate: content.recordingDate || '',
          operator: content.operator || '',
          productionBy: content.productionBy || '',
          hinglishSentForEditingDate: content.hinglishSentForEditingDate || '',
          hinglishEditDate: content.hinglishEditDate || '',
          hinglishVideoLength: content.hinglishVideoLength || '',
          hinglishReceivedDate: content.hinglishReceivedDate || '',
          hinglishEditor: content.hinglishEditor || '',
          hinglishEditingStatus: content.hinglishEditingStatus || '',
          hinglishQcStatus: content.hinglishQcStatus || '',
          hinglishQcDoneBy: content.hinglishQcDoneBy || '',
          englishMp3GenerateStatus: content.englishMp3GenerateStatus || '',
          englishMp3GenerateBy: content.englishMp3GenerateBy || '',
          englishSentForEditingDate: content.englishSentForEditingDate || '',
          englishEditDate: content.englishEditDate || '',
          englishEditTime: content.englishEditTime || '',
          englishReceivedDate: content.englishReceivedDate || '',
          englishEditor: content.englishEditor || '',
          englishEditingStatus: content.englishEditingStatus || '',
          sequenceNumber: (index + 1).toString(),
        }));

        // Load existing contents and show edit view directly
        setLoadedContents(existingContents);
        setShowContentEditView(true);
        toast.info(`Loaded ${existingContents.length} existing contents`);
      } else {
        // No existing contents, show upload dialog
        setOpenContentDialog(true);
      }
    } catch (error) {
      console.error('Error fetching contents:', error);
      toast.error('Failed to fetch existing contents. You can add new ones.');
      setOpenContentDialog(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Download sample content CSV template
  const handleDownloadTemplate = () => {
    const template = [
      {
        contentTitle: 'Introduction Video',
        contentType: 'Video',
        contentUrl: 'https://example.com/video1.mp4',
        duration: '10',
        sequenceNumber: '1',
        description: 'Introduction to the chapter',
      },
      {
        contentTitle: 'Practice Slides',
        contentType: 'PPT',
        contentUrl: 'https://example.com/slides1.pptx',
        duration: '15',
        sequenceNumber: '2',
        description: 'Practice exercises',
      },
      {
        contentTitle: 'Reading Material',
        contentType: 'PDF',
        contentUrl: 'https://example.com/document.pdf',
        duration: '20',
        sequenceNumber: '3',
        description: 'Additional reading material',
      },
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Contents Template');

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'sample_chapter_contents_template.xlsx');

    toast.success('Template downloaded successfully!');
  };

  // Handle CSV file upload for contents
  const handleContentCSVUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }

    // Handle Excel files
    if (file.name.endsWith('.xlsx')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet);

          processContentData(jsonData);
        } catch (error) {
          console.error('Excel parsing error:', error);
          toast.error('Error parsing Excel file');
        }
      };
      reader.readAsArrayBuffer(file);
      return;
    }

    // Handle CSV files
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        processContentData(results.data);
      },
      error: (error) => {
        console.error('CSV parsing error:', error);
        toast.error('Error reading CSV file');
      },
    });
  };

  // Process and validate content data
  const processContentData = (data) => {
    try {
      if (data.length === 0) {
        toast.error('File is empty');
        return;
      }

      const requiredFields = ['contentTitle', 'contentType'];
      const headers = Object.keys(data[0]);
      const missingFields = requiredFields.filter((field) => !headers.includes(field));

      if (missingFields.length > 0) {
        toast.error(`Missing required fields: ${missingFields.join(', ')}`);
        return;
      }

      // Validate and prepare contents with unique IDs
      const validContents = data.map((row, index) => {
        if (!row.contentTitle || !row.contentType) {
          throw new Error(`Row ${index + 1}: contentTitle and contentType are required`);
        }
        return {
          id: Date.now() + index, // Temporary unique ID for DataGrid
          contentTitle: row.contentTitle,
          contentType: row.contentType,
          contentUrl: row.contentUrl || '',
          duration: row.duration || '',
          sequenceNumber: row.sequenceNumber || (index + 1).toString(),
          description: row.description || '',
        };
      });

      // Store contents and show edit view
      setLoadedContents(validContents);
      setShowContentEditView(true);
      setOpenContentDialog(false);

      toast.success(`Successfully loaded ${validContents.length} contents. You can now edit and save.`);

      // Reset file input
      if (contentFileInputRef.current) {
        contentFileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Content processing error:', error);
      toast.error(error.message || 'Error processing content data');
    }
  };

  // Handle adding new empty content row
  const handleAddEmptyContent = () => {
    const newContent = {
      id: Date.now(),
      projectChapterId: selectedChapterId,
      contentTitle: '',
      contentDescription: '',
      subject: '',
      contentType: '',
      contentUrl: '',
      chapterName: '',
      teacher: '',
      timing: '',
      recordingDate: '',
      operator: '',
      productionBy: '',
      hinglishSentForEditingDate: '',
      hinglishEditDate: '',
      hinglishVideoLength: '',
      hinglishReceivedDate: '',
      hinglishEditor: '',
      hinglishEditingStatus: '',
      hinglishQcStatus: '',
      hinglishQcDoneBy: '',
      englishMp3GenerateStatus: '',
      englishMp3GenerateBy: '',
      englishSentForEditingDate: '',
      englishEditDate: '',
      englishEditTime: '',
      englishReceivedDate: '',
      englishEditor: '',
      englishEditingStatus: '',
      sequenceNumber: (loadedContents.length + 1).toString(),
    };
    setLoadedContents([...loadedContents, newContent]);
    toast.success('New content row added');
  };

  // Handle deleting content row
  const handleDeleteContentRow = (id) => {
    setLoadedContents(loadedContents.filter((content) => content.id !== id));
    toast.success('Content removed');
  };

  // Handle content row update - Auto-save individual field changes
  const handleContentRowUpdate = async (newRow, oldRow) => {
    try {
      // Check if this is an existing content (has database ID)
      const isExistingContent = newRow.id && newRow.id < 1000000000000;

      if (isExistingContent) {
        // Save individual field update to API
        const payload = {
          projectChapterId: selectedChapterId,
          chapterContentEditingDTOList: [{
            id: newRow.id,
            contentTitle: newRow.contentTitle,
            contentDescription: newRow.contentDescription,
            subject: newRow.subject,
            contentType: newRow.contentType,
            contentUrl: newRow.contentUrl,
            chapterName: newRow.chapterName,
            teacher: newRow.teacher,
            timing: newRow.timing,
            recordingDate: newRow.recordingDate,
            operator: newRow.operator,
            productionBy: newRow.productionBy,
            hinglishSentForEditingDate: newRow.hinglishSentForEditingDate,
            hinglishEditDate: newRow.hinglishEditDate,
            hinglishVideoLength: newRow.hinglishVideoLength,
            hinglishReceivedDate: newRow.hinglishReceivedDate,
            hinglishEditor: newRow.hinglishEditor,
            hinglishEditingStatus: newRow.hinglishEditingStatus,
            hinglishQcStatus: newRow.hinglishQcStatus,
            hinglishQcDoneBy: newRow.hinglishQcDoneBy,
            englishMp3GenerateStatus: newRow.englishMp3GenerateStatus,
            englishMp3GenerateBy: newRow.englishMp3GenerateBy,
            englishSentForEditingDate: newRow.englishSentForEditingDate,
            englishEditDate: newRow.englishEditDate,
            englishEditTime: newRow.englishEditTime,
            englishReceivedDate: newRow.englishReceivedDate,
            englishEditor: newRow.englishEditor,
            englishEditingStatus: newRow.englishEditingStatus,
            sequenceNumber: newRow.sequenceNumber,
          }],
        };

        const response = await saveChapterContents(payload);

        if (response?.data?.status === 'SUCCESS') {
          toast.success('Content updated successfully!');
          // Update local state
          const updatedContents = loadedContents.map((content) =>
            content.id === newRow.id ? newRow : content
          );
          setLoadedContents(updatedContents);
          return newRow;
        }

        toast.error(response?.data?.message || 'Failed to update content');
        return oldRow; // Revert to old value
      }

      // New content (not saved yet), just update local state
      const updatedContents = loadedContents.map((content) =>
        content.id === newRow.id ? newRow : content
      );
      setLoadedContents(updatedContents);
      return newRow;
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Error updating content');
      return oldRow; // Revert to old value on error
    }
  };

  // Handle saving NEW contents to API (only for unsaved rows)
  const handleSaveNewContents = async () => {
    // Filter only new contents (temporary IDs)
    const newContents = loadedContents.filter((content) => content.id >= 1000000000000);

    if (newContents.length === 0) {
      toast.warning('No new contents to save');
      return;
    }

    // Validate required fields
    const invalidRows = newContents.filter(
      (content) => !content.contentTitle || !content.contentType
    );

    if (invalidRows.length > 0) {
      toast.error('Please fill in contentTitle and contentType for all new rows');
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        projectChapterId: selectedChapterId,
        chapterContentEditingDTOList: newContents.map((content) => ({
          contentTitle: content.contentTitle,
          contentDescription: content.contentDescription,
          subject: content.subject,
          contentType: content.contentType,
          contentUrl: content.contentUrl,
          chapterName: content.chapterName,
          teacher: content.teacher,
          timing: content.timing,
          recordingDate: content.recordingDate,
          operator: content.operator,
          productionBy: content.productionBy,
          hinglishSentForEditingDate: content.hinglishSentForEditingDate,
          hinglishEditDate: content.hinglishEditDate,
          hinglishVideoLength: content.hinglishVideoLength,
          hinglishReceivedDate: content.hinglishReceivedDate,
          hinglishEditor: content.hinglishEditor,
          hinglishEditingStatus: content.hinglishEditingStatus,
          hinglishQcStatus: content.hinglishQcStatus,
          hinglishQcDoneBy: content.hinglishQcDoneBy,
          englishMp3GenerateStatus: content.englishMp3GenerateStatus,
          englishMp3GenerateBy: content.englishMp3GenerateBy,
          englishSentForEditingDate: content.englishSentForEditingDate,
          englishEditDate: content.englishEditDate,
          englishEditTime: content.englishEditTime,
          englishReceivedDate: content.englishReceivedDate,
          englishEditor: content.englishEditor,
          englishEditingStatus: content.englishEditingStatus,
          sequenceNumber: content.sequenceNumber,
        })),
      };

      console.log('Saving new contents:', payload);

      const response = await saveChapterContents(payload);

      if (response?.data?.status === 'SUCCESS') {
        toast.success(`${newContents.length} new content(s) saved successfully!`);
        // Reload contents from API
        const refreshResponse = await getChapterContentEditing(selectedChapterId);
        if (refreshResponse?.data?.status === 'SUCCESS') {
          const refreshedContents = refreshResponse.data.response.map((content, index) => ({
            id: content.id,
            projectChapterId: content.projectChapterId,
            contentTitle: content.contentTitle || '',
            contentDescription: content.contentDescription || '',
            subject: content.subject || '',
            contentType: content.contentType || '',
            contentUrl: content.contentUrl || '',
            chapterName: content.chapterName || '',
            teacher: content.teacher || '',
            timing: content.timing || '',
            recordingDate: content.recordingDate || '',
            operator: content.operator || '',
            productionBy: content.productionBy || '',
            hinglishSentForEditingDate: content.hinglishSentForEditingDate || '',
            hinglishEditDate: content.hinglishEditDate || '',
            hinglishVideoLength: content.hinglishVideoLength || '',
            hinglishReceivedDate: content.hinglishReceivedDate || '',
            hinglishEditor: content.hinglishEditor || '',
            hinglishEditingStatus: content.hinglishEditingStatus || '',
            hinglishQcStatus: content.hinglishQcStatus || '',
            hinglishQcDoneBy: content.hinglishQcDoneBy || '',
            englishMp3GenerateStatus: content.englishMp3GenerateStatus || '',
            englishMp3GenerateBy: content.englishMp3GenerateBy || '',
            englishSentForEditingDate: content.englishSentForEditingDate || '',
            englishEditDate: content.englishEditDate || '',
            englishEditTime: content.englishEditTime || '',
            englishReceivedDate: content.englishReceivedDate || '',
            englishEditor: content.englishEditor || '',
            englishEditingStatus: content.englishEditingStatus || '',
            sequenceNumber: (index + 1).toString(),
          }));
          setLoadedContents(refreshedContents);
        }
      } else {
        toast.error(response?.data?.message || 'Failed to save new contents');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Error saving new contents');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle row updates for editable fields (non-status fields)
  const handleProcessRowUpdate = async (newRow, oldRow) => {
    try {
      setIsLoading(true);

      // Prepare update payload
      const updatePayload = {
        projectChapterId: newRow.projectChapterId,
        chapterName: newRow.chapterName,
        designerName: newRow.designerName,
        noOfPpt: newRow.noOfPpt,
        noOfVideo: newRow.noOfVideo,
        pptVideoStartDate: newRow.pptVideoStartDate,
        pptVideoEndDate: newRow.pptVideoEndDate,
        microplanRecieved: newRow.microplanRecieved,
        microplanName: newRow.microplanName,
        microplanDate: newRow.microplanDate,
        microplanClassworkDate: newRow.microplanClassworkDate,
        pdfResource: newRow.pdfResource,
        recordingTeacher: newRow.recordingTeacher,
        recordingDate: newRow.recordingDate,
        projectId,
      };

      const response = await updateProjectChapter(updatePayload);

      if (response?.data?.status === 'SUCCESS') {
        toast.success('Chapter updated successfully!');

        // Update local state
        setChapters((prev) =>
          prev.map((row) => (row.projectChapterId === newRow.projectChapterId ? newRow : row))
        );
        setAllChapters((prev) =>
          prev.map((row) => (row.projectChapterId === newRow.projectChapterId ? newRow : row))
        );

        setIsLoading(false);
        return newRow;
      }

      toast.error(response?.data?.message || 'Failed to update chapter');
      setIsLoading(false);
      return oldRow;
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Error updating chapter');
      setIsLoading(false);
      return oldRow;
    }
  };

  const handleProcessRowUpdateError = (error) => {
    console.error('Row update error:', error);
    toast.error('Failed to update chapter');
  };

  // Open dialog to update completion status
  const openUpdateStatusDialog = (type, projectChapterId) => {
    setStatusDialogData({
      type,
      projectChapterId,
      noOfContent: '',
      completedBy: '',
    });
    setOpenStatusDialog(true);
  };

  // Handle status update submission
  const handleStatusUpdateSubmit = async () => {
    const { type, projectChapterId, noOfContent, completedBy } = statusDialogData;

    if (!noOfContent || !completedBy) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    try {
      let response;

      // Determine which API to call based on type
      if (type === 'PPT' || type === 'VIDEO') {
        // Use updateDataAsset for PPT and VIDEO
        const payload = {
          assetType: type,
          completedBy,
          noOfContent: Number(noOfContent),
          projectChapterId: Number(projectChapterId),
        };
        response = await updateDataAsset(payload);
      } else {
        // Use updateDepartmentVideo for RECORDING, EDITING, DUBBING, ENGLISH_EDITING
        const payload = {
          workType: type,
          completedBy,
          noOfContent: Number(noOfContent),
          projectChapterId: Number(projectChapterId),
        };
        response = await updateDepartmentVideo(payload);
      }

      if (response?.data?.status === 'SUCCESS') {
        toast.success('Completion status updated successfully!');
        setOpenStatusDialog(false);
        // Refresh chapter data
        fetchProjectChapters(projectId);
      } else {
        toast.error(response?.data?.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Error updating completion status');
    } finally {
      setIsLoading(false);
    }
  };

  // Status cell renderer with colored badges and update button
  const renderStatusCell = (params) => {
    const status = params.value?.toUpperCase();
    const updateType = params.colDef.field; // Get the column field name
    let color = 'default';
    let icon = null;

    if (status === 'COMPLETED' || status === 'DONE') {
      color = 'success';
      icon = <FaCheckCircle style={{ fontSize: '14px', marginRight: '4px' }} />;
    } else if (status === 'IN_PROGRESS') {
      color = 'primary';
    } else if (status === 'PLANNING') {
      color = 'warning';
    } else if (status === 'IN_EDITING') {
      color = 'info';
    }

    // Map column field to API type
    const typeMap = {
      pptStatus: 'PPT',
      videoStatus: 'VIDEO',
      recordingStatus: 'RECORDING',
      editingStatus: 'EDITING',
      dubbingStatus: 'DUBBING',
      englishEditingStatus: 'ENGLISH_EDIT',
    };

    const apiType = typeMap[updateType];

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          label={
            <span style={{ display: 'flex', alignItems: 'center' }}>
              {icon}
              {params.value || '-'}
            </span>
          }
          color={color}
          size="small"
          sx={{ fontWeight: 500 }}
        />
        {apiType && (
          <IconButton
            size="small"
            color="primary"
            title="Update Completion Status"
            onClick={(e) => {
              e.stopPropagation();
              openUpdateStatusDialog(apiType, params.row.projectChapterId);
            }}
            sx={{ padding: '2px' }}
          >
            <FaPlusCircle size={16} />
          </IconButton>
        )}
      </Box>
    );
  };

  // Date cell renderer
  const renderDateCell = (params) => {
    if (!params.value) return '-';
    const date = new Date(params.value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
  };

  // Action buttons cell renderer
  const renderActionsCell = (params) => {
    return (
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'center' }}>
        <FaEye
          title="View Chapter Details"
          style={{ cursor: 'pointer', color: '#1976d2', fontSize: '18px' }}
          onClick={() => handleViewChapter(params.row.projectChapterId)}
        />
        <FaEdit
          title="Edit"
          style={{ cursor: 'pointer', color: '#2e7d32', fontSize: '18px' }}
          onClick={() => handleEditChapter(params.row.projectChapterId)}
        />
        <FaList
          title="Add Contents"
          style={{ cursor: 'pointer', color: '#ed6c02', fontSize: '18px' }}
          onClick={() => handleOpenContentDialog(params.row.projectChapterId, params.row.chapterName)}
        />
        <FaTrash
          title="Delete"
          style={{ cursor: 'pointer', color: '#d32f2f', fontSize: '18px' }}
          onClick={() => handleDeleteChapter(params.row.projectChapterId)}
        />
      </div>
    );
  };

  // Column definitions
  const columns = [
    {
      field: 'chapterName',
      headerName: 'Chapter Name',
      width: 120,
      editable: false,
    },
    {
      field: 'designerName',
      headerName: 'Designer Name',
      width: 150,
      editable: true,
    },
    {
      field: 'pptProgress',
      headerName: 'PPTs (Uploaded / Total)',
      width: 180,
      editable: false,
      valueGetter: (params) => {
        const left = params.row.noOfPptLeft ?? 0;
        const total = params.row.noOfPpt ?? 0;
        const uploaded = total - left;
        return `${uploaded} / ${total}`;
      },
    },
    {
      field: 'videoProgress',
      headerName: 'Videos (Uploaded / Total)',
      width: 180,
      editable: false,
      valueGetter: (params) => {
        const left = params.row.noOfVideoLeft ?? 0;
        const total = params.row.noOfVideo ?? 0;
        const uploaded = total - left;
        return `${uploaded} / ${total}`;
      },
    },
    {
      field: 'pptStatus',
      headerName: 'PPT Status',
      width: 200,
      editable: false,
      renderCell: renderStatusCell,
    },
    {
      field: 'videoStatus',
      headerName: 'Video Status',
      width: 200,
      editable: false,
      renderCell: renderStatusCell,
    },
    {
      field: 'pptVideoStartDate',
      headerName: 'PPT Video Start Date',
      width: 160,
      editable: true,
      type: 'date',
      renderCell: renderDateCell,
      valueGetter: (params) => {
        if (!params.value) return null;
        return new Date(params.value);
      },
    },
    {
      field: 'pptVideoEndDate',
      headerName: 'PPT Video End Date',
      width: 160,
      editable: true,
      type: 'date',
      renderCell: renderDateCell,
      valueGetter: (params) => {
        if (!params.value) return null;
        return new Date(params.value);
      },
    },
    {
      field: 'microplanRecieved',
      headerName: 'Microplan Received',
      width: 160,
      editable: true,
      type: 'singleSelect',
      valueOptions: ['YES', 'NO', 'PENDING'],
    },
    {
      field: 'microplanName',
      headerName: 'Microplan Name',
      width: 180,
      editable: true,
    },
    {
      field: 'recordingStatus',
      headerName: 'Recording Status',
      width: 210,
      editable: false,
      renderCell: renderStatusCell,
    },
    {
      field: 'editingStatus',
      headerName: 'Editing Status',
      width: 200,
      editable: false,
      renderCell: renderStatusCell,
    },
    {
      field: 'dubbingStatus',
      headerName: 'Dubbing Status',
      width: 200,
      editable: false,
      renderCell: renderStatusCell,
    },
    {
      field: 'englishEditingStatus',
      headerName: 'English Editing Status',
      width: 230,
      editable: false,
      renderCell: renderStatusCell,
    },
    {
      field: 'status',
      headerName: 'Overall Status',
      width: 150,
      editable: false,
      renderCell: renderStatusCell,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: renderActionsCell,
    },
  ];

  const today = new Date();

  // Summary Counts
  const totalCount = allChapters.length;
  const planningCount = allChapters.filter((c) => c.status === 'PLANNING').length;
  const progressCount = allChapters.filter((c) => c.status === 'IN_PROGRESS').length;
  const editingCount = allChapters.filter((c) => c.status === 'IN_EDITING').length;
  const doneCount = allChapters.filter((c) => c.status === 'DONE' || c.status === 'COMPLETED').length;

  return (
    <>
      {isLoading && <Loader />}
      <ToastContainer />

      <Box sx={{ padding: '20px 40px' }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => navigate('/dashboard/projectlist')}
          sx={{
            borderColor: '#ccc',
            '&:hover': { borderColor: '#888' },
          }}
        >
          ← Back
        </Button>
      </Box>

      <Container sx={{ mb: 6 }}>
        {/* Project Header */}
        {projectData && (
          <Paper
            elevation={4}
            sx={{
              p: 3,
              mb: 4,
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #E3F2FD, #E1F5FE)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <div>
              <Typography variant="h4" fontWeight={600} color="#0D47A1">
                {projectData.name}
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                <b>Status:</b>{' '}
                <span className="font-semibold text-blue-700 bg-blue-100 px-3 py-1 rounded-lg">
                  {projectData.projectStatus}
                </span>
              </Typography>
            </div>
            <div>
              <Typography
                variant="body1"
                sx={{
                  color:
                    Array.isArray(projectData.deadline) &&
                    new Date(projectData.deadline[0], projectData.deadline[1] - 1, projectData.deadline[2]) < today
                      ? 'red'
                      : 'inherit',
                }}
              >
                <b>Deadline:</b>{' '}
                {Array.isArray(projectData.deadline)
                  ? `${projectData.deadline[2]}-${projectData.deadline[1]}-${projectData.deadline[0]}`
                  : '-'}
              </Typography>
              <Typography variant="body1">
                <b>Days Left:</b> {projectData.noDayForDeadline}
              </Typography>
            </div>
          </Paper>
        )}

        {/* Summary Cards */}
        <Grid container spacing={3} mb={4}>
          {[
            { title: 'Total Chapters', count: totalCount, gradient: 'linear-gradient(135deg, #E3F2FD, #BBDEFB)' },
            { title: 'Planning', count: planningCount, gradient: 'linear-gradient(135deg, #EDE7F6, #D1C4E9)' },
            { title: 'In Progress', count: progressCount, gradient: 'linear-gradient(135deg, #E1F5FE, #B3E5FC)' },
            { title: 'In Editing', count: editingCount, gradient: 'linear-gradient(135deg, #FFF3E0, #FFE0B2)' },
            { title: 'Completed', count: doneCount, gradient: 'linear-gradient(135deg, #E8F5E9, #C8E6C9)' },
          ].map((card, idx) => (
            <Grid item xs={12} sm={6} md={2.4} key={idx}>
              <Paper
                elevation={4}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderRadius: '16px',
                  background: card.gradient,
                  color: '#333',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': { transform: 'translateY(-5px)', boxShadow: 6 },
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: '600' }}>
                  {card.title}
                </Typography>
                <Typography variant="h4" sx={{ mt: 1, fontWeight: '700' }}>
                  {card.count}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Create Chapter Button */}
        <Stack direction="row" justifyContent="flex-end" mb={3}>
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            sx={{ borderRadius: '8px', textTransform: 'none', px: 3 }}
            onClick={() => handleCreateChapter(projectId, projectData?.name)}
          >
            Create Chapter
          </Button>
        </Stack>

        {/* Excel-like DataGrid */}
        <Paper sx={{ height: 650, width: '100%' }}>
          <DataGrid
            rows={chapters}
            columns={columns}
            getRowId={(row) => row.projectChapterId}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25 },
              },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            checkboxSelection
            disableRowSelectionOnClick
            processRowUpdate={handleProcessRowUpdate}
            onProcessRowUpdateError={handleProcessRowUpdateError}
            slots={{
              toolbar: () => <CustomToolbar chapters={chapters} projectData={projectData} />,
            }}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: '2px solid #1976d2',
                outlineOffset: '-1px',
              },
              '& .MuiDataGrid-cell--editable': {
                backgroundColor: '#f5f9ff',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#e3f2fd',
                },
              },
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: '#1976d2',
                color: 'white',
                fontWeight: 'bold',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 'bold',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          />
        </Paper>

        {/* Update Completion Status Dialog */}
        <Dialog open={openStatusDialog} onClose={() => setOpenStatusDialog(false)}>
          <DialogTitle>Update {statusDialogData.type} Completion Status</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ width: 400, mt: 2 }}>
              <TextField
                label="No. of Content"
                type="number"
                value={statusDialogData.noOfContent}
                onChange={(e) =>
                  setStatusDialogData((prev) => ({ ...prev, noOfContent: e.target.value }))
                }
                fullWidth
                required
              />
              <TextField
                label="Completed By"
                value={statusDialogData.completedBy}
                onChange={(e) =>
                  setStatusDialogData((prev) => ({ ...prev, completedBy: e.target.value }))
                }
                fullWidth
                required
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenStatusDialog(false)}>Cancel</Button>
            <Button onClick={handleStatusUpdateSubmit} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Chapter Contents Dialog */}
        <Dialog
          open={openContentDialog}
          onClose={() => setOpenContentDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FaList style={{ color: '#ed6c02' }} />
              <Typography variant="h6">
                Add Contents to: {selectedChapterName}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              {/* Hidden file input */}
              <input
                type="file"
                ref={contentFileInputRef}
                accept=".csv,.xlsx"
                style={{ display: 'none' }}
                onChange={handleContentCSVUpload}
              />

              {/* Instructions */}
              <Paper sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <strong>Instructions:</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" component="div">
                  1. Download the sample template below<br />
                  2. Fill in your chapter contents<br />
                  3. Upload the completed file (CSV or Excel)<br />
                  <br />
                  <strong>Required fields:</strong> contentTitle, contentType<br />
                  <strong>Optional fields:</strong> contentUrl, duration, sequenceNumber, description
                </Typography>
              </Paper>

              {/* Note about existing contents */}
              <Paper sx={{ p: 1.5, bgcolor: '#e3f2fd', borderRadius: 2, border: '1px solid #90caf9' }}>
                <Typography variant="body2" color="primary">
                  <strong>Note:</strong> Uploading a new file will replace all existing contents for this chapter.
                </Typography>
              </Paper>

              {/* Download Template Button */}
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FaFileExcel />}
                onClick={handleDownloadTemplate}
                fullWidth
              >
                Download Sample Template
              </Button>

              {/* Upload File Button */}
              <Button
                variant="contained"
                color="success"
                startIcon={<Iconify icon="eva:cloud-upload-fill" />}
                onClick={() => contentFileInputRef.current?.click()}
                fullWidth
                sx={{ py: 1.5 }}
              >
                Upload Contents File (CSV/Excel)
              </Button>

              {/* Divider */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1, height: '1px', bgcolor: '#ddd' }} />
                <Typography variant="body2" color="text.secondary">OR</Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: '#ddd' }} />
              </Box>

              {/* Manual Entry Button */}
              <Button
                variant="outlined"
                color="warning"
                startIcon={<FaPlusCircle />}
                onClick={() => {
                  setOpenContentDialog(false);
                  setShowContentEditView(true);
                  setLoadedContents([{
                    id: Date.now(),
                    projectChapterId: selectedChapterId,
                    contentTitle: '',
                    contentDescription: '',
                    subject: '',
                    contentType: '',
                    contentUrl: '',
                    chapterName: '',
                    teacher: '',
                    timing: '',
                    recordingDate: '',
                    operator: '',
                    productionBy: '',
                    hinglishSentForEditingDate: '',
                    hinglishEditDate: '',
                    hinglishVideoLength: '',
                    hinglishReceivedDate: '',
                    hinglishEditor: '',
                    hinglishEditingStatus: '',
                    hinglishQcStatus: '',
                    hinglishQcDoneBy: '',
                    englishMp3GenerateStatus: '',
                    englishMp3GenerateBy: '',
                    englishSentForEditingDate: '',
                    englishEditDate: '',
                    englishEditTime: '',
                    englishReceivedDate: '',
                    englishEditor: '',
                    englishEditingStatus: '',
                    sequenceNumber: '1',
                  }]);
                }}
                fullWidth
              >
                Add Contents Manually
              </Button>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenContentDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Content Edit View Dialog */}
        <Dialog
          open={showContentEditView}
          onClose={() => {
            setShowContentEditView(false);
            setLoadedContents([]);
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FaList style={{ color: '#ed6c02' }} />
                <Typography variant="h6">
                  {loadedContents.some(c => c.id && c.id < 1000000000000)
                    ? 'Edit Contents for:'
                    : 'Add Contents to:'} {selectedChapterName}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Chip
                  label={`${loadedContents.length} contents`}
                  color="primary"
                  size="small"
                />
                {loadedContents.some(c => c.id && c.id < 1000000000000) && (
                  <Chip
                    label="Editing Existing"
                    color="success"
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 2 }}>
              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<FaPlusCircle />}
                  onClick={handleAddEmptyContent}
                >
                  Add New Content
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<FaFileExcel />}
                  onClick={handleDownloadTemplate}
                >
                  Download Template
                </Button>
              </Box>

              {/* DataGrid for Contents */}
              <Paper sx={{ height: 500, width: '100%' }}>
                <DataGrid
                  rows={loadedContents}
                  columns={[
                    {
                      field: 'sequenceNumber',
                      headerName: 'Seq #',
                      width: 80,
                      editable: true,
                    },
                    {
                      field: 'contentTitle',
                      headerName: 'Content Title *',
                      width: 200,
                      editable: true,
                    },
                    {
                      field: 'contentDescription',
                      headerName: 'Description',
                      width: 200,
                      editable: true,
                    },
                    {
                      field: 'subject',
                      headerName: 'Subject',
                      width: 150,
                      editable: true,
                    },
                    {
                      field: 'contentType',
                      headerName: 'Type *',
                      width: 120,
                      editable: true,
                      type: 'singleSelect',
                      valueOptions: ['Video', 'PPT', 'PDF', 'Quiz', 'Lab', 'Document'],
                    },
                    {
                      field: 'contentUrl',
                      headerName: 'URL',
                      width: 250,
                      editable: true,
                    },
                    {
                      field: 'chapterName',
                      headerName: 'Chapter Name',
                      width: 180,
                      editable: true,
                    },
                    {
                      field: 'teacher',
                      headerName: 'Teacher',
                      width: 150,
                      editable: true,
                    },
                    {
                      field: 'timing',
                      headerName: 'Timing (mins)',
                      width: 120,
                      editable: true,
                    },
                    {
                      field: 'recordingDate',
                      headerName: 'Recording Date',
                      width: 140,
                      editable: true,
                      type: 'date',
                      valueGetter: (params) => params.value ? new Date(params.value) : null,
                    },
                    {
                      field: 'operator',
                      headerName: 'Operator',
                      width: 130,
                      editable: true,
                    },
                    {
                      field: 'productionBy',
                      headerName: 'Production By',
                      width: 150,
                      editable: true,
                    },
                    {
                      field: 'hinglishSentForEditingDate',
                      headerName: 'Hinglish Sent Date',
                      width: 160,
                      editable: true,
                      type: 'date',
                      valueGetter: (params) => params.value ? new Date(params.value) : null,
                    },
                    {
                      field: 'hinglishEditDate',
                      headerName: 'Hinglish Edit Date',
                      width: 160,
                      editable: true,
                      type: 'date',
                      valueGetter: (params) => params.value ? new Date(params.value) : null,
                    },
                    {
                      field: 'hinglishVideoLength',
                      headerName: 'Hinglish Video Length',
                      width: 170,
                      editable: true,
                    },
                    {
                      field: 'hinglishReceivedDate',
                      headerName: 'Hinglish Received Date',
                      width: 180,
                      editable: true,
                      type: 'date',
                      valueGetter: (params) => params.value ? new Date(params.value) : null,
                    },
                    {
                      field: 'hinglishEditor',
                      headerName: 'Hinglish Editor',
                      width: 150,
                      editable: true,
                    },
                    {
                      field: 'hinglishEditingStatus',
                      headerName: 'Hinglish Edit Status',
                      width: 160,
                      editable: true,
                    },
                    {
                      field: 'hinglishQcStatus',
                      headerName: 'Hinglish QC Status',
                      width: 160,
                      editable: true,
                    },
                    {
                      field: 'hinglishQcDoneBy',
                      headerName: 'Hinglish QC By',
                      width: 150,
                      editable: true,
                    },
                    {
                      field: 'englishMp3GenerateStatus',
                      headerName: 'English MP3 Status',
                      width: 170,
                      editable: true,
                    },
                    {
                      field: 'englishMp3GenerateBy',
                      headerName: 'English MP3 By',
                      width: 150,
                      editable: true,
                    },
                    {
                      field: 'englishSentForEditingDate',
                      headerName: 'English Sent Date',
                      width: 160,
                      editable: true,
                      type: 'date',
                      valueGetter: (params) => params.value ? new Date(params.value) : null,
                    },
                    {
                      field: 'englishEditDate',
                      headerName: 'English Edit Date',
                      width: 160,
                      editable: true,
                      type: 'date',
                      valueGetter: (params) => params.value ? new Date(params.value) : null,
                    },
                    {
                      field: 'englishEditTime',
                      headerName: 'English Edit Time',
                      width: 150,
                      editable: true,
                    },
                    {
                      field: 'englishReceivedDate',
                      headerName: 'English Received Date',
                      width: 180,
                      editable: true,
                      type: 'date',
                      valueGetter: (params) => params.value ? new Date(params.value) : null,
                    },
                    {
                      field: 'englishEditor',
                      headerName: 'English Editor',
                      width: 150,
                      editable: true,
                    },
                    {
                      field: 'englishEditingStatus',
                      headerName: 'English Edit Status',
                      width: 160,
                      editable: true,
                    },
                    {
                      field: 'actions',
                      headerName: 'Actions',
                      width: 100,
                      sortable: false,
                      filterable: false,
                      disableColumnMenu: true,
                      renderCell: (params) => (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteContentRow(params.row.id)}
                        >
                          <FaTrash />
                        </IconButton>
                      ),
                    },
                  ]}
                  getRowId={(row) => row.id}
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 10 },
                    },
                  }}
                  pageSizeOptions={[5, 10, 25]}
                  processRowUpdate={handleContentRowUpdate}
                  onProcessRowUpdateError={(error) => {
                    console.error('Row update error:', error);
                    toast.error('Failed to update row');
                  }}
                  sx={{
                    '& .MuiDataGrid-cell:focus': {
                      outline: '2px solid #1976d2',
                      outlineOffset: '-1px',
                    },
                    '& .MuiDataGrid-cell--editable': {
                      backgroundColor: '#f5f9ff',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#e3f2fd',
                      },
                    },
                    '& .MuiDataGrid-columnHeader': {
                      backgroundColor: '#ed6c02',
                      color: 'white',
                      fontWeight: 'bold',
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: '#fff8f0',
                    },
                  }}
                />
              </Paper>

              {/* Info Box */}
              <Paper sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FaCheckCircle style={{ color: '#1976d2' }} />
                  <Typography variant="body2" color="text.secondary">
                    <strong>Auto-Save Enabled:</strong> Existing content changes are saved automatically when you edit any cell.
                    New rows (with no database ID) need to be saved manually.
                  </Typography>
                </Stack>
              </Paper>

              {/* Warning for unsaved new contents */}
              {loadedContents.some(c => c.id >= 1000000000000) && (
                <Paper sx={{ p: 2, bgcolor: '#fff8e1', borderRadius: 2, border: '1px solid #ffc107' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="warning.dark">
                      <strong>⚠️ Unsaved Changes:</strong> You have {loadedContents.filter(c => c.id >= 1000000000000).length} new row(s) that need to be saved.
                    </Typography>
                  </Stack>
                </Paper>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={() => {
                setShowContentEditView(false);
                setLoadedContents([]);
              }}
            >
              Close
            </Button>
            {loadedContents.some(c => c.id >= 1000000000000) && (
              <Button
                variant="contained"
                color="success"
                onClick={handleSaveNewContents}
                disabled={isLoading}
                startIcon={<FaCheckCircle />}
              >
                {isLoading ? 'Saving...' : `Save ${loadedContents.filter(c => c.id >= 1000000000000).length} New Content(s)`}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}
